import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Employee } from '../../models/employee.models';
import { EmployeeProfile } from '../../models/employee-profile.model';
import { EmployeeService } from '../../../services/employee.service';
import { Subscription } from 'rxjs';
import { formatShortDate } from '../../../shared/utils/date.util';
import { PhoneValidator } from '../../../shared/validators/phone.validator';

/**
 * Displays an employee profile inside a modal dialog with loading/error handling and display helpers.
 * @component
 * @selector app-employee-profile-modal
 * @example
 *   <app-employee-profile-modal [isVisible]="true" [employee]="selectedEmployee" (closeModal)="onModalClosed()"></app-employee-profile-modal>
 *
 * This component is responsible for presenting detailed employee profile information in a modal.
 * It coordinates fetching the profile via EmployeeService, managing loading and error states,
 * and providing utility methods for formatting and display (initials, full name, ID, phone, date, location).
 *
 * Key behaviors:
 * - Explicit loading of profile data via loadEmployeeProfile() to avoid brief automatic loading states.
 * - Maintains local state: employeeProfile, isLoading, and errorMessage.
 * - Cancels and unsubscribes from in-flight requests on destroy or when resetting state.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Standard initialization (no automatic profile load to avoid flicker).
 * - ngOnDestroy(): Unsubscribes from any active Subscription to prevent memory leaks.
 *
 * @input {boolean} isVisible - Controls modal visibility. Setter avoids automatically triggering a reload to prevent brief loading states.
 * @input {Employee | null} employee - The Employee object to display (may be null). Setter avoids automatically triggering a reload to prevent brief loading states.
 * @output {EventEmitter<void>} closeModal - Emitted when the modal is closed (via onClose()); consumers should hide the modal in response.
 *
 * Important methods:
 * - loadEmployeeProfile(): Fetches the employee profile by employee.id, sets isLoading, handles response and errors, and unsubscribes previous requests to avoid duplicates.
 * - onClose(): Resets modal state and emits closeModal to notify parent to close the modal.
 * - resetModalState(): Clears employeeProfile, isLoading, errorMessage and unsubscribes the active subscription.
 * - onModalClick(event: Event): Stops propagation to prevent backdrop clicks from closing the modal unintentionally.
 * - getEmployeeInitials(firstName: string, lastName: string): Returns uppercase initials for avatar display.
 * - getEmployeeId(employee: Employee): Returns emp_id from API response or 'N/A' if unavailable.
 * - getEmployeeFullName(employee: Employee): Returns a concatenated "First Last" or 'Unknown Employee' if missing.
 * - formatPhoneNumber(phoneNumber: string): Formats US numbers via PhoneValidator.formatUsaPhoneDisplay.
 * - formatDate(dateString: string): Formats a date using the shared formatShortDate helper.
 * - getLocationDisplay(locationName: string): Returns the location name or 'Not specified' when empty.
 */
@Component({
  selector: 'app-employee-profile-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './employee-profile-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./employee-profile-modal.component.scss'],
})
export class EmployeeProfileModalComponent implements OnDestroy {
  @Input() set isVisible(value: boolean) {
    this._isVisible = value;
    // Remove automatic loading to prevent brief loading state
    // if (value && this.employee) {
    //   this.loadEmployeeProfile();
    // }
  }
  get isVisible(): boolean {
    return this._isVisible;
  }
  @Input() set employee(value: Employee | null) {
    this._employee = value;
    // Remove automatic loading to prevent brief loading state
    // if (value && this.isVisible) {
    //   this.loadEmployeeProfile();
    // }
  }
  get employee(): Employee | null {
    return this._employee;
  }
  @Output() closeModal = new EventEmitter<void>();

  private _employee: Employee | null = null;
  private _isVisible = false;

  employeeProfile: EmployeeProfile | null = null;
  isLoading = false;
  errorMessage = '';
  private subscription?: Subscription;

  constructor(private employeeService: EmployeeService) {}

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onClose(): void {
    this.resetModalState();
    this.closeModal.emit();
  }

  private resetModalState(): void {
    this.employeeProfile = null;
    this.isLoading = false;
    this.errorMessage = '';
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Loads the current employee's profile into the component state, managing loading state, errors, and unsubscribing any previous request.
   * @example
   * oadEmployeeProfile()
   * undefined
   * @returns {void} No return value; updates component properties (employeeProfile, isLoading, errorMessage) and manages the subscription to employeeService.getEmployeeProfile.
   **/
  loadEmployeeProfile(): void {
    if (!this.employee?.id) {
      this.errorMessage = 'Employee ID not found';
      return;
    }

    // Don't reload if already loading
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.employeeProfile = null;

    // Unsubscribe from previous request if any
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.employeeService.getEmployeeProfile(this.employee.id).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.status && response.data) {
          this.employeeProfile = response.data;
        } else {
          this.errorMessage = response.message || 'Failed to load employee profile';
        }
      },
      error: error => {
        this.isLoading = false;
        console.error('Error loading employee profile:', error);
        this.errorMessage = 'Failed to load employee profile. Please try again.';
      },
    });
  }

  getEmployeeInitials(firstName: string, lastName: string): string {
    const first = firstName.charAt(0).toUpperCase();
    const last = lastName.charAt(0).toUpperCase();
    return `${first}${last}`;
  }

  getEmployeeId(employee: Employee): string {
    // Use emp_id directly from API response, no generation
    return employee?.emp_id || 'N/A';
  }

  getEmployeeFullName(employee: Employee): string {
    if (employee?.first_name && employee?.last_name) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    return 'Unknown Employee';
  }

  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber?.trim()) {
      return '';
    }
    return PhoneValidator.formatUsaPhoneDisplay(phoneNumber);
  }

  formatDate(dateString: string): string {
    return formatShortDate(dateString);
  }

  getLocationDisplay(locationName: string): string {
    if (locationName && locationName.trim()) {
      return locationName;
    }
    return 'Not specified';
  }
}
